import { useEffect, useMemo } from "react";
import { Box } from "@chakra-ui/react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.heat";

// Guanacaste province bbox (tight).
// South: tip of Nicoya peninsula. North: Nicaragua border.
// West: Pacific coast. East: border with Alajuela.
const GUANACASTE_BOUNDS_TUPLE: [[number, number], [number, number]] = [
	[9.65, -85.95], // SW
	[11.22, -84.85], // NE
];
const GUANACASTE_BOUNDS = L.latLngBounds(GUANACASTE_BOUNDS_TUPLE);
// Geographic centroid of the province, roughly Liberia–Nicoya corridor.
const GUANACASTE_CENTER: [number, number] = [10.45, -85.4];

// Pan limits cover all of Costa Rica so the user can zoom out and see
// the (few) reports outside Guanacaste.
const MAX_BOUNDS: L.LatLngBoundsExpression = [
	[7.2, -86.5],
	[11.8, -82.2],
];

export function isInGuanacaste(lat: number, lng: number): boolean {
	return GUANACASTE_BOUNDS.contains(L.latLng(lat, lng));
}

interface HeatLayerProps {
	points: [number, number, number?][];
	radius?: number;
	blur?: number;
	maxZoom?: number;
}

const HeatLayer = ({ points, radius = 25, blur = 18, maxZoom = 13 }: HeatLayerProps) => {
	const map = useMap();
	useEffect(() => {
		if (!points.length) return;
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const heat = (L as any).heatLayer(points, {
			radius,
			blur,
			maxZoom,
			minOpacity: 0.35,
			gradient: {
				0.2: "#3B82F6",
				0.4: "#22C55E",
				0.6: "#EAB308",
				0.8: "#F97316",
				1.0: "#EF4444",
			},
		}).addTo(map);

		return () => {
			map.removeLayer(heat);
		};
	}, [map, points, radius, blur, maxZoom]);
	return null;
};

const FitToGuanacaste = () => {
	const map = useMap();
	useEffect(() => {
		// Center on the province, then snap to bounds so it always fills the view.
		map.setView(GUANACASTE_CENTER, 9, { animate: false });
		map.fitBounds(GUANACASTE_BOUNDS_TUPLE, { padding: [12, 12], animate: false });
	}, [map]);
	return null;
};

interface HeatmapMapProps {
	points: [number, number, number?][];
	height?: number | string;
}

export const HeatmapMap = ({ points, height = 480 }: HeatmapMapProps) => (
	<Box
		h={typeof height === "number" ? `${height}px` : height}
		w="100%"
		borderRadius="xl"
		overflow="hidden"
		borderWidth="1px"
		borderColor="gray.200"
		boxShadow="sm"
	>
		<MapContainer
			center={GUANACASTE_CENTER}
			zoom={9}
			minZoom={7}
			maxZoom={16}
			maxBounds={MAX_BOUNDS}
			maxBoundsViscosity={1.0}
			style={{ height: "100%", width: "100%" }}
			scrollWheelZoom
			worldCopyJump={false}
		>
			<TileLayer
				attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
				url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
			/>
			<FitToGuanacaste />
			<HeatLayer points={points} />
		</MapContainer>
	</Box>
);
